import React, { useEffect, useMemo, useState } from 'react';
import { FileSpreadsheet, FileText } from 'lucide-react';

const thumbnailCache = new Map();

const isAbortError = (error) => error?.name === 'AbortError';

const buildAuthorizedUrl = (fileUrl, token) => {
    if (!fileUrl) return '';
    if (!token) return fileUrl;
    const separator = fileUrl.includes('?') ? '&' : '?';
    return `${fileUrl}${separator}token=${encodeURIComponent(token)}`;
};

const getFileExt = (fileName) =>
    fileName?.split('.').pop()?.toLowerCase() || '';

const isExcelFile = (mimeType, fileName) => {
    const ext = getFileExt(fileName);
    return mimeType?.includes('spreadsheet') || mimeType?.includes('excel') ||
        ['xls', 'xlsx', 'csv'].includes(ext);
};

const isWordFile = (mimeType, fileName) => {
    const ext = getFileExt(fileName);
    return mimeType?.includes('word') || mimeType?.includes('document') ||
        ['doc', 'docx'].includes(ext);
};

const isDocPreviewable = (mimeType, fileName) =>
    isExcelFile(mimeType, fileName) || isWordFile(mimeType, fileName);

const getCacheKey = ({ file, fileUuid, fileName, width }) => {
    if (file instanceof File) {
        return `doc:local:${file.name}:${file.size}:${file.lastModified}:${width}`;
    }
    if (fileUuid) {
        return `doc:remote:${fileUuid}:${width}`;
    }
    return `doc:fallback:${fileName || 'doc'}:${width}`;
};

const fetchFileData = async ({ file, fileUrl, token, signal }) => {
    if (file instanceof File) {
        return file.arrayBuffer();
    }
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await fetch(buildAuthorizedUrl(fileUrl, token), { headers, signal });
    if (!response.ok) {
        throw new Error('No se pudo cargar el archivo');
    }
    return response.arrayBuffer();
};

const renderExcelPreview = async ({ file, fileUrl, token, width, maxRows = 8, signal }) => {
    const XLSX = (await import('xlsx')).default || (await import('xlsx'));
    const data = await fetchFileData({ file, fileUrl, token, signal });
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const workbook = XLSX.read(data, { type: 'array', sheetRows: maxRows + 1 });
    const sheetName = workbook.SheetNames[0];
    if (!sheetName) return { type: 'excel', rows: [], sheetName: '' };

    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

    return {
        type: 'excel',
        rows: rows.slice(0, maxRows),
        sheetName,
        totalSheets: workbook.SheetNames.length
    };
};

const renderWordPreview = async ({ file, fileUrl, token, signal }) => {
    const mammoth = (await import('mammoth')).default || (await import('mammoth'));
    const data = await fetchFileData({ file, fileUrl, token, signal });
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    const result = await mammoth.convertToHtml(
        { arrayBuffer: data },
        { styleMap: ['p => p', 'b => strong', 'i => em'] }
    );

    return {
        type: 'word',
        html: result.value
    };
};

const DocPreview = ({
    file,
    fileUuid,
    fileUrl,
    token,
    fileName,
    mimeType,
    width = 220,
    maxRows = 8,
    mode = 'thumbnail', // 'thumbnail' | 'full'
    className = '',
    imageClassName = '',
    badgeClassName = '',
    placeholderClassName = ''
}) => {
    const [previewData, setPreviewData] = useState(null);
    const [hasError, setHasError] = useState(false);

    const resolvedMimeType = mimeType || file?.type || '';
    const resolvedFileName = fileName || file?.name || '';
    const fileType = isExcelFile(resolvedMimeType, resolvedFileName) ? 'excel' : 'word';

    const cacheKey = useMemo(
        () => getCacheKey({ file, fileUuid, fileName: resolvedFileName, width }),
        [file, fileUuid, resolvedFileName, width]
    );

    useEffect(() => {
        const cached = thumbnailCache.get(cacheKey);
        if (cached) {
            setPreviewData(cached);
            setHasError(false);
            return;
        }

        const abortController = new AbortController();
        let cancelled = false;

        setPreviewData(null);
        setHasError(false);

        const renderFn = fileType === 'excel' ? renderExcelPreview : renderWordPreview;

        renderFn({
            file,
            fileUrl,
            token,
            width,
            maxRows: mode === 'full' ? 50 : maxRows,
            signal: abortController.signal
        })
            .then((data) => {
                if (cancelled || !data) return;
                thumbnailCache.set(cacheKey, data);
                setPreviewData(data);
            })
            .catch((error) => {
                if (cancelled || isAbortError(error)) return;
                console.error('Error generando preview de documento:', error);
                setHasError(true);
            });

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [cacheKey, file, fileUrl, token, width, maxRows, fileType, mode]);

    const badgeText = fileType === 'excel' ? 'XLSX' : 'DOCX';
    const BadgeIcon = fileType === 'excel' ? FileSpreadsheet : FileText;

    if (mode === 'full') {
        return (
            <div className={`doc-preview-full ${className}`}>
                {previewData?.type === 'excel' && previewData.rows.length > 0 ? (
                    <div className="doc-preview-full-excel">
                        {previewData.totalSheets > 1 && (
                            <div className="doc-preview-sheet-info">
                                Hoja: {previewData.sheetName} ({previewData.totalSheets} hojas)
                            </div>
                        )}
                        <div className="doc-preview-table-wrapper">
                            <table className="doc-preview-table doc-preview-table-full">
                                <tbody>
                                    {previewData.rows.map((row, ri) => (
                                        <tr key={ri} className={ri === 0 ? 'doc-preview-header-row' : ''}>
                                            {Array.isArray(row) && row.map((cell, ci) => (
                                                ri === 0 ? (
                                                    <th key={ci}>{cell != null ? String(cell) : ''}</th>
                                                ) : (
                                                    <td key={ci}>{cell != null ? String(cell) : ''}</td>
                                                )
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : previewData?.type === 'word' && previewData.html ? (
                    <div
                        className="doc-preview-full-word"
                        dangerouslySetInnerHTML={{ __html: previewData.html }}
                    />
                ) : hasError ? (
                    <div className="doc-preview-full-error">
                        <BadgeIcon size={40} />
                        <span>No se pudo previsualizar</span>
                    </div>
                ) : (
                    <div className="doc-preview-full-loading">
                        <BadgeIcon size={32} />
                        <span>Cargando previsualización...</span>
                    </div>
                )}
            </div>
        );
    }

    // Thumbnail mode
    return (
        <div className={className}>
            {previewData ? (
                <div className={imageClassName}>
                    {previewData.type === 'excel' && previewData.rows.length > 0 ? (
                        <div className="doc-preview-mini-excel">
                            <table className="doc-preview-table">
                                <tbody>
                                    {previewData.rows.slice(0, maxRows).map((row, ri) => (
                                        <tr key={ri} className={ri === 0 ? 'doc-preview-header-row' : ''}>
                                            {Array.isArray(row) && row.slice(0, 6).map((cell, ci) => (
                                                <td key={ci}>{cell != null ? String(cell) : ''}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : previewData.type === 'word' && previewData.html ? (
                        <div
                            className="doc-preview-mini-word"
                            dangerouslySetInnerHTML={{ __html: previewData.html }}
                        />
                    ) : null}
                </div>
            ) : (
                <div className={placeholderClassName}>
                    <BadgeIcon size={24} />
                    <span>{hasError ? badgeText : 'Cargando'}</span>
                </div>
            )}
            <span className={badgeClassName}>{badgeText}</span>
        </div>
    );
};

export { isExcelFile, isWordFile, isDocPreviewable };
export default DocPreview;
